import { useMemo, useState, useCallback, useEffect } from 'react';
import * as d3 from 'd3';
import { TabState, createColumnId, createColumnName, createDatasetId, ColumnSchema, PrimaryDataSet, ContourLine, DataSet } from '../../../../types';
import type { GLMDiagnosticsMetadata, ResidualType } from '../../../../types/tabs/glm-diagnostics.types';
import type { GLMParameters } from '../../../../types/model.types';
import { useProjectData } from "../../../stores/hooks/useProjectData";
import { useDatasetSelection } from '../../../stores/hooks/useDatasetSelection';
import { useReportActions } from '../../../contexts/ReportContext';
import { ReportActions } from '../../ReportActions';
import { ContextHelpButtonSimple } from '../../ContextHelp';
import { ScatterPlot } from '../../ScatterPlot';
import { QQPlot } from '../../visualization/QQPlot';
import { computeCookDistanceContour } from '../../../utils/glm-diagnostics';
import { evaluateDerivedDataset } from '../../../utils/evaluateDataset';
import logger from '../../../utils/logger';
import * as styles from './GLMDiagnosticsTabContent.css';

/**
 * GLMDiagnosticsTabContent
 *
 * GLMモデルの診断タブ - 4つの診断プロットを表示
 */

interface GLMDiagnosticsTabContentProps {
  tab: TabState;
}

export function GLMDiagnosticsTabContent({ tab }: GLMDiagnosticsTabContentProps) {
  const { project, getAllDatasets } = useProjectData();

  // metadataからmodelIdを取得
  const metadata = tab.metadata as GLMDiagnosticsMetadata | undefined;
  const modelId = metadata?.modelId;

  // 残差タイプの状態管理
  const [residualType, setResidualType] = useState<ResidualType>('deviance');

  // レポート関連の機能を取得
  const {
    availableReports,
    onAddToReport,
    onCreateNewReport
  } = useReportActions();

  // モデルを取得
  const model = useMemo(() => {
    if (!modelId || !project?.models) {
      logger.warn('[GLMDiagnostics] Model ID not found in metadata');
      return null;
    }

    const foundModel = project.models[modelId];
    if (!foundModel) {
      logger.warn('[GLMDiagnostics] Model not found:', { modelId });
      return null;
    }

    if (foundModel.type !== 'glm') {
      logger.error('[GLMDiagnostics] Model is not a GLM model:', { modelId, type: foundModel.type });
      return null;
    }

    return foundModel;
  }, [modelId, project]);

  // GLMパラメータを取得
  const glmParameters = useMemo(() => {
    if (!model) return null;
    return model.parameters as GLMParameters;
  }, [model]);

  // 診断サマリーを取得
  const diagnosticSummary = useMemo(() => {
    if (!glmParameters?.diagnosticSummary) {
      logger.warn('[GLMDiagnostics] Diagnostic summary not available in model');
      return null;
    }
    return glmParameters.diagnosticSummary;
  }, [glmParameters]);

  // データセットを取得（元のトレーニングデータセット）
  const dataset = useMemo(() => {
    if (!model?.metadata?.trainingDatasetId) return null;

    const allDatasets = getAllDatasets();
    const foundDataset = allDatasets.find(ds => ds.id === model.metadata.trainingDatasetId);

    if (!foundDataset) {
      logger.warn('[GLMDiagnostics] Dataset not found:', { datasetId: model.metadata.trainingDatasetId });
    }

    return foundDataset || null;
  }, [model, getAllDatasets]);

  // 診断データセットを取得
  const diagnosticDataset = useMemo(() => {
    if (!diagnosticSummary?.diagnosticDatasetId) return null;

    const allDatasets = getAllDatasets();
    const foundDataset = allDatasets.find(ds => ds.id === diagnosticSummary.diagnosticDatasetId);

    if (!foundDataset) {
      logger.warn('[GLMDiagnostics] Diagnostic dataset not found:', {
        diagnosticDatasetId: diagnosticSummary.diagnosticDatasetId
      });
    }

    return foundDataset || null;
  }, [diagnosticSummary, getAllDatasets]);

  // 診断データセットのデータをロード（lazy evaluation）
  const [diagnosticDatasetWithData, setDiagnosticDatasetWithData] = useState<DataSet | null>(null);

  useEffect(() => {
    if (!diagnosticDataset) {
      setDiagnosticDatasetWithData(null);
      return;
    }

    // Derived datasetでdataがない場合は評価する
    if (diagnosticDataset.type === 'derived' && !diagnosticDataset.data) {
      logger.info('[GLMDiagnostics] Evaluating diagnostic dataset (lazy evaluation)', {
        diagnosticDatasetId: diagnosticDataset.id,
        operation: diagnosticDataset.operation,
        parentDatasetIds: diagnosticDataset.parentDatasetIds
      });
      const allDatasetsRecord: Record<string, DataSet> = {};
      getAllDatasets().forEach(ds => {
        allDatasetsRecord[ds.id] = ds;
      });

      logger.info('[GLMDiagnostics] Available models:', {
        modelIds: project?.models ? Object.keys(project.models) : [],
        operationModelId: diagnosticDataset.operation?.type === 'glm_diagnostics' ? diagnosticDataset.operation.modelId : undefined
      });

      // 親データセットの列IDを確認
      const parentDataset = allDatasetsRecord[diagnosticDataset.parentDatasetIds[0]];
      if (parentDataset) {
        logger.info('[GLMDiagnostics] Parent dataset columns:', {
          datasetId: parentDataset.id,
          weightCol: parentDataset.schema.columns.find(c => c.name === 'weight')?.id,
          mpgCol: parentDataset.schema.columns.find(c => c.name === 'mpg')?.id
        });
      }

      // GLM diagnostics操作を評価（modelsはストアから取得される）
      evaluateDerivedDataset(diagnosticDataset, allDatasetsRecord)
        .then(evaluated => {
          logger.info('[GLMDiagnostics] Diagnostic dataset evaluated successfully');
          setDiagnosticDatasetWithData(evaluated);
        })
        .catch(error => {
          logger.error('[GLMDiagnostics] Failed to evaluate diagnostic dataset:', error);
          setDiagnosticDatasetWithData(null);
        });
    } else {
      setDiagnosticDatasetWithData(diagnosticDataset);
    }
  }, [diagnosticDataset, getAllDatasets, project]);

  // プロット用データセットと行インデックスマッピングを作成
  const { plotDataset, plotIndexToOriginalIndex } = useMemo((): {
    plotDataset: PrimaryDataSet | null;
    plotIndexToOriginalIndex: number[];
  } => {
    if (!diagnosticDatasetWithData || !diagnosticSummary || !modelId) {
      return { plotDataset: null, plotIndexToOriginalIndex: [] };
    }

    try {
      // 診断統計量の列ID
      const fittedValuesColId = createColumnId('fitted_values');
      const devianceResidualsColId = createColumnId('deviance_residuals');
      const pearsonResidualsColId = createColumnId('pearson_residuals');
      const standardizedResidualsColId = createColumnId('standardized_residuals');
      const leverageColId = createColumnId('leverage');

      // 診断データセットから列データを取得
      const fittedValuesData = (diagnosticDatasetWithData.type === 'primary' || diagnosticDatasetWithData.type === 'derived')
        ? (diagnosticDatasetWithData.data?.[fittedValuesColId] as (number | null)[] || [])
        : [];

      // 完全観測のインデックスを抽出（fitted_valuesがnullでない行）
      const completeIndices: number[] = [];
      fittedValuesData.forEach((val, idx) => {
        if (val !== null) {
          completeIndices.push(idx);
        }
      });

      // 各列から完全観測のみを抽出
      const extractCompleteValues = (colId: string): number[] => {
        const data = (diagnosticDatasetWithData.type === 'primary' || diagnosticDatasetWithData.type === 'derived')
          ? diagnosticDatasetWithData.data
          : undefined;
        if (!data) return [];

        const colData = data[colId as keyof typeof data] as (number | null)[] | undefined;
        if (!colData) return [];

        return completeIndices.map(i => colData[i] as number);
      };

      const fittedValues = extractCompleteValues(fittedValuesColId);
      const devianceResiduals = extractCompleteValues(devianceResidualsColId);
      const pearsonResiduals = extractCompleteValues(pearsonResidualsColId);
      const standardizedResiduals = extractCompleteValues(standardizedResidualsColId);
      const leverage = extractCompleteValues(leverageColId);

      // Scale-Location用の√|標準化残差|を計算
      const sqrtAbsStdResiduals = standardizedResiduals.map(r => Math.sqrt(Math.abs(r)));

      // 列スキーマを定義
      const ratioScale = { scale: 'ratio' as const, inferredAutomatically: true };

      const columns: ColumnSchema[] = [
        {
          id: fittedValuesColId,
          name: createColumnName('Fitted Values'),
          type: 'float64',
          scale: ratioScale,
          nullable: false
        },
        {
          id: devianceResidualsColId,
          name: createColumnName('Deviance Residuals'),
          type: 'float64',
          scale: ratioScale,
          nullable: false
        },
        {
          id: pearsonResidualsColId,
          name: createColumnName('Pearson Residuals'),
          type: 'float64',
          scale: ratioScale,
          nullable: false
        },
        {
          id: standardizedResidualsColId,
          name: createColumnName('Standardized Residuals'),
          type: 'float64',
          scale: ratioScale,
          nullable: false
        },
        {
          id: leverageColId,
          name: createColumnName('Leverage'),
          type: 'float64',
          scale: ratioScale,
          nullable: false
        },
        {
          id: createColumnId('sqrt_abs_std_residuals'),
          name: createColumnName('√|Standardized Residuals|'),
          type: 'float64',
          scale: ratioScale,
          nullable: false
        }
      ];

      // プロット用データセット（完全観測のみ）
      const plotDs: PrimaryDataSet = {
        id: createDatasetId(`${modelId}_diagnostics_plot`),
        name: 'GLM Diagnostics (Plot Data)',
        type: 'primary',
        schema: { columns },
        data: {
          [fittedValuesColId]: fittedValues,
          [devianceResidualsColId]: devianceResiduals,
          [pearsonResidualsColId]: pearsonResiduals,
          [standardizedResidualsColId]: standardizedResiduals,
          [leverageColId]: leverage,
          [createColumnId('sqrt_abs_std_residuals')]: sqrtAbsStdResiduals
        },
        rowCount: completeIndices.length,
        metadata: {
          originalFileName: `${modelId}_diagnostics.csv`,
          importedAt: new Date().toISOString()
        }
      };

      return { plotDataset: plotDs, plotIndexToOriginalIndex: completeIndices };
    } catch (error) {
      logger.error('[GLMDiagnostics] Failed to create plot dataset:', error);
      return { plotDataset: null, plotIndexToOriginalIndex: [] };
    }
  }, [diagnosticDatasetWithData, diagnosticSummary, modelId]);

  // データセット選択機能（元のデータセットに対して）
  const { selectedRows, selectRows } = useDatasetSelection(dataset?.id || '');

  // 現在の残差タイプに応じた残差列IDを取得
  const currentResidualsColumnId = useMemo(() => {
    return residualType === 'deviance'
      ? createColumnId('deviance_residuals')
      : createColumnId('pearson_residuals');
  }, [residualType]);

  // Add All Plots用のレポート選択状態
  const [selectedReportId, setSelectedReportId] = useState<string>('');

  // 全プロットをレポートに追加する
  const handleAddAllPlotsToReport = useCallback(() => {
    if (!plotDataset || !selectedReportId || !onAddToReport) return;

    const plots = [
      {
        type: 'residual_plot' as const,
        title: 'Residuals vs Fitted Values',
        residualPlotData: {
          predictedColumn: createColumnId('fitted_values'),
          residualColumn: currentResidualsColumnId,
          dataset: plotDataset
        }
      },
      {
        type: 'qq_plot' as const,
        title: 'Normal Q-Q Plot',
        qqPlotData: {
          columnName: createColumnId('standardized_residuals'),
          dataset: plotDataset
        }
      },
      {
        type: 'scatter_plot' as const,
        title: 'Scale-Location Plot',
        scatterPlotData: {
          xColumn: createColumnId('fitted_values'),
          yColumn: createColumnId('sqrt_abs_std_residuals'),
          dataset: plotDataset
        }
      },
      {
        type: 'scatter_plot' as const,
        title: 'Residuals vs Leverage',
        scatterPlotData: {
          xColumn: createColumnId('leverage'),
          yColumn: createColumnId('standardized_residuals'),
          dataset: plotDataset
        }
      }
    ];

    // 選択されたレポートに全プロットを追加
    plots.forEach(plot => {
      onAddToReport(selectedReportId, plot);
    });

    logger.info('[GLMDiagnostics] Added all plots to report', {
      reportId: selectedReportId,
      plotCount: plots.length
    });
  }, [plotDataset, currentResidualsColumnId, selectedReportId, onAddToReport]);

  // モデル式を生成（列IDを列名に変換、切片項を明示的に表示）
  const modelFormula = useMemo(() => {
    if (!model || !dataset) return '';

    const metadata = model.metadata as { target?: string; features?: string[] };
    const includeIntercept = model.hyperparameters?.includeIntercept ?? true;

    // 列IDから列名を取得するヘルパー関数
    const getColumnName = (columnId: string): string => {
      const column = dataset.schema.columns.find(col => col.id === columnId);
      return column?.name || columnId;
    };

    const yName = metadata.target ? getColumnName(metadata.target) : 'Y';

    // 切片項を明示的に含める
    const interceptTerm = includeIntercept ? '1' : '0';
    const featureTerms = metadata.features?.map(getColumnName) || [];
    const xTerms = [interceptTerm, ...featureTerms].join(' + ');

    const familyName = glmParameters?.family || '';
    const linkName = glmParameters?.link || '';

    return `${yName} ~ ${xTerms} (${familyName}, ${linkName})`;
  }, [model, dataset, glmParameters]);

  // データセット情報を生成
  const datasetInfo = useMemo(() => {
    if (!diagnosticSummary) return '';

    const n = diagnosticSummary.nObservations;
    const nIncomplete = diagnosticSummary.nIncomplete;
    const datasetName = dataset?.name || 'Unknown';

    if (nIncomplete > 0) {
      return `${datasetName} (n=${n}, after removing ${nIncomplete} incomplete observations)`;
    }
    return `${datasetName} (n=${n})`;
  }, [diagnosticSummary, dataset]);

  // Cook's distance等高線を計算
  const cookContourLines = useMemo((): ContourLine[] => {
    if (!glmParameters) return [];

    // 予測変数の数（xColumnsの数 + 1 for intercept）
    const metadata = model?.metadata as { features?: string[] };
    const p = (metadata.features?.length || 0) + 1;

    // 等高線はプロット表示範囲全体に描画する
    // 実データのmaxLeverageは小さい（例: 0.05）ため、固定値0.5を使用
    const maxLeverage = 0.5;

    // 0.5と1.0の等高線を計算（上下2本ずつ、計4本）
    const contours: ContourLine[] = [];

    // D = 0.5 等高線
    const contour05 = computeCookDistanceContour(0.5, p, maxLeverage, 100);
    contours.push({
      points: contour05.map(pt => ({ x: pt.h, y: pt.r })),  // 上側（正の残差）
      label: "D = 0.5",
      style: { stroke: '#ff9800', strokeWidth: 1.5, opacity: 0.6, strokeDasharray: '4 2' }
    });
    contours.push({
      points: contour05.map(pt => ({ x: pt.h, y: -pt.r })),  // 下側（負の残差）
      style: { stroke: '#ff9800', strokeWidth: 1.5, opacity: 0.6, strokeDasharray: '4 2' }
    });

    // D = 1.0 等高線
    const contour10 = computeCookDistanceContour(1.0, p, maxLeverage, 100);
    contours.push({
      points: contour10.map(pt => ({ x: pt.h, y: pt.r })),  // 上側（正の残差）
      label: "D = 1.0",
      style: { stroke: '#f44336', strokeWidth: 1.5, opacity: 0.6, strokeDasharray: '4 2' }
    });
    contours.push({
      points: contour10.map(pt => ({ x: pt.h, y: -pt.r })),  // 下側（負の残差）
      style: { stroke: '#f44336', strokeWidth: 1.5, opacity: 0.6, strokeDasharray: '4 2' }
    });

    return contours;
  }, [glmParameters, model]);

  // Residuals vs Leverageプロットの表示範囲を計算
  // データ点を見やすく、かつD=0.5等高線がちらっと見える程度に調整
  const residualsVsLeverageDomain = useMemo((): { xDomain: [number, number]; yDomain: [number, number] } | null => {
    if (!plotDataset || cookContourLines.length === 0) return null;

    // データの範囲を取得
    const leverageColId = createColumnId('leverage');
    const standardizedResidualsColId = createColumnId('standardized_residuals');

    const leverageData = plotDataset.data[leverageColId] as number[];
    const stdResData = plotDataset.data[standardizedResidualsColId] as number[];

    const leverageExtent = d3.extent(leverageData) as [number, number];
    const stdResExtent = d3.extent(stdResData) as [number, number];

    // X軸範囲: データの最大leverage × 1.2（少し余白）
    const xMin = 0;
    const xMax = leverageExtent[1] * 1.2;

    // X軸右端でのD=0.5等高線のr値を計算
    // r = sqrt((0.5 * p * (1 - h)^2) / h)
    const metadata = model?.metadata as { features?: string[] };
    const p = (metadata.features?.length || 0) + 1;
    const h = xMax;
    const contourRAtXMax = Math.sqrt((0.5 * p * (1 - h) * (1 - h)) / h);

    // Y軸範囲: データ範囲 + D=0.5等高線がちらっと見える程度
    const yMin = Math.min(stdResExtent[0], -contourRAtXMax) * 1.1;
    const yMax = Math.max(stdResExtent[1], contourRAtXMax) * 1.1;

    return {
      xDomain: [xMin, xMax],
      yDomain: [yMin, yMax]
    };
  }, [plotDataset, cookContourLines, model]);

  // エラー表示
  if (!modelId) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>Model ID not found in tab metadata</div>
      </div>
    );
  }

  if (!model) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>Model not found: {modelId}</div>
      </div>
    );
  }

  if (!diagnosticSummary) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          Diagnostic summary not available for this model.
          <br />
          Please re-run the GLM analysis to generate diagnostic statistics.
        </div>
      </div>
    );
  }

  // diagnosticSummaryがあるのにdatasetが見つからない場合はエラー
  if (diagnosticSummary.diagnosticDatasetId && !diagnosticDataset) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          Diagnostic dataset not found: {diagnosticSummary.diagnosticDatasetId}
          <br />
          The diagnostic dataset may have been deleted or is unavailable.
        </div>
      </div>
    );
  }

  if (!diagnosticDatasetWithData) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading diagnostic dataset...</div>
      </div>
    );
  }

  if (!dataset || !plotDataset) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>Dataset not found for this model</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h2>GLM Diagnostics</h2>

      {/* ヘッダー：モデル情報 */}
      <div className={styles.header}>
        <div className={styles.modelInfo}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Model:</span>
            <span className={styles.infoValue}>{modelFormula}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Data:</span>
            <span className={styles.infoValue}>{datasetInfo}</span>
          </div>
        </div>
      </div>

      {/* Overdispersion警告 */}
      {diagnosticSummary.dispersionParameter > 1.5 &&
       (glmParameters?.family === 'poisson' || glmParameters?.family === 'binomial') && (
        <div className={styles.overdispersionWarning}>
          <span className={styles.warningIcon}>⚠</span>
          <span>
            Overdispersion detected (φ = {diagnosticSummary.dispersionParameter.toFixed(2)}).
            Consider using a Negative Binomial model (now available in GLM tab).
          </span>
          <ContextHelpButtonSimple
            ariaLabel="Overdispersion help - variance exceeds model prediction"
            helpContent={
              <div>
                <h3>Overdispersion</h3>
                <p>
                  <strong>Overdispersion</strong> occurs when the observed variance in the data is larger than
                  what the model predicts. For Poisson and Binomial models, this violates a key assumption.
                </p>

                <h4>What is φ (dispersion parameter)?</h4>
                <p>
                  φ measures how much the actual variance exceeds the theoretical variance:
                </p>
                <ul>
                  <li><strong>φ ≈ 1</strong>: Model assumptions hold (variance matches expected)</li>
                  <li><strong>φ {`>`} 1</strong>: Overdispersion (variance {`>`} expected)</li>
                  <li><strong>φ {`>`} 1.5</strong>: Significant overdispersion - action recommended</li>
                </ul>

                <h4>Why is it a problem?</h4>
                <p>
                  Overdispersion causes:
                </p>
                <ul>
                  <li><strong>Underestimated standard errors</strong>: Confidence intervals too narrow</li>
                  <li><strong>Inflated significance</strong>: p-values too small, false positives</li>
                  <li><strong>Poor predictions</strong>: Uncertainty underestimated</li>
                </ul>

                <h4>How to address it?</h4>
                <p>
                  For <strong>Poisson models</strong> (count data):
                </p>
                <ul>
                  <li><strong>Negative Binomial</strong>: Models variance separately from mean (recommended)</li>
                  <li>Allows variance to be larger than mean: Var(Y) = μ + μ²/θ</li>
                  <li>More flexible for handling large overdispersion</li>
                </ul>
                <p>
                  For <strong>Binomial models</strong> (proportion data):
                </p>
                <ul>
                  <li><strong>Beta-Binomial</strong>: Models variability in success probability</li>
                  <li>Allows for correlation or clustering within groups</li>
                </ul>

                <h4>Common causes:</h4>
                <ul>
                  <li>Missing important predictors</li>
                  <li>Clustering or correlation in observations</li>
                  <li>Excess zeros in count data (consider Zero-Inflated models)</li>
                  <li>Incorrect distributional assumption</li>
                </ul>
              </div>
            }
          />
        </div>
      )}

      {/* 残差タイプ選択 */}
      <div className={styles.residualTypeSelector}>
        <span className={styles.selectorLabel}>Residual Type:</span>
        <label className={styles.radioLabel}>
          <input
            type="radio"
            value="deviance"
            checked={residualType === 'deviance'}
            onChange={(e) => setResidualType(e.target.value as ResidualType)}
          />
          Deviance
        </label>
        <label className={styles.radioLabel}>
          <input
            type="radio"
            value="pearson"
            checked={residualType === 'pearson'}
            onChange={(e) => setResidualType(e.target.value as ResidualType)}
          />
          Pearson
        </label>
      </div>

      {/* 4つの診断プロット（2x2グリッド） */}
      <div className={styles.plotGrid}>
        {/* Residuals vs Fitted Values */}
        <div className={styles.plotContainer}>
          <div className={styles.plotTitleRow}>
            <h3 className={styles.plotTitle}>Residuals vs Fitted</h3>
            <ContextHelpButtonSimple
              ariaLabel="Residuals vs Fitted help - checking linearity assumption"
              helpContent={
                <div>
                  <h3>Residuals vs Fitted Values</h3>
                  <p>
                    This plot checks the <strong>linearity assumption</strong> of your model by plotting residuals against predicted values.
                  </p>
                  <h4>What to look for:</h4>
                  <ul>
                    <li><strong>Good fit</strong>: Points scattered randomly around the horizontal line at zero, with no clear pattern</li>
                    <li><strong>Non-linearity</strong>: Curved pattern (U-shape, inverted U, etc.) suggests the model may be missing non-linear terms</li>
                    <li><strong>Non-constant variance</strong>: Funnel shape (spreading or narrowing) indicates heteroscedasticity</li>
                  </ul>
                  <h4>Why it matters:</h4>
                  <p>
                    If residuals show a pattern, the model is systematically missing something. The residuals contain information that could improve predictions.
                  </p>
                </div>
              }
            />
          </div>
          <ReportActions
            availableReports={availableReports}
            onAddToReport={onAddToReport}
            onCreateNewReport={onCreateNewReport}
            actionData={{
              type: 'residual_plot',
              title: 'Residuals vs Fitted Values',
              residualPlotData: {
                predictedColumn: createColumnId('fitted_values'),
                residualColumn: currentResidualsColumnId,
                dataset: plotDataset
              }
            }}
          >
            <div className={styles.plotWrapper}>
              <ScatterPlot
                dataset={plotDataset}
                xColumn={createColumnId('fitted_values')}
                yColumn={currentResidualsColumnId}
                width={400}
                height={300}
                selectedRows={selectedRows}
                onPointClick={selectRows}
                referenceLines={[
                  { type: 'horizontal', value: 0, style: { stroke: 'gray', strokeWidth: 1, strokeDasharray: '4 4' } }
                ]}
              />
            </div>
          </ReportActions>
        </div>

        {/* Normal Q-Q Plot */}
        <div className={styles.plotContainer}>
          <div className={styles.plotTitleRow}>
            <h3 className={styles.plotTitle}>Normal Q-Q</h3>
            <ContextHelpButtonSimple
              ariaLabel="Normal Q-Q help - checking normality of residuals"
              helpContent={
                <div>
                  <h3>Normal Q-Q Plot</h3>
                  <p>
                    This plot checks if the <strong>standardized residuals follow a normal distribution</strong> by comparing them to theoretical normal quantiles.
                  </p>
                  <h4>What to look for:</h4>
                  <ul>
                    <li><strong>Normal distribution</strong>: Points lie close to the diagonal reference line</li>
                    <li><strong>Heavy tails</strong>: Points deviate at the ends (more extreme values than expected)</li>
                    <li><strong>Light tails</strong>: Points curve toward the line at the ends</li>
                    <li><strong>Skewness</strong>: Systematic departure from the line</li>
                  </ul>
                  <h4>Why only for Gaussian family?</h4>
                  <p>
                    For Gaussian GLMs, residuals are expected to be normally distributed. For other families (Poisson, Binomial, Gamma),
                    the underlying distribution is different, making this plot misleading rather than helpful.
                  </p>
                  <h4>Why it matters:</h4>
                  <p>
                    Outliers (points far from the line) can have large influence on the model. Severe departures suggest the model
                    assumptions may not hold.
                  </p>
                </div>
              }
            />
          </div>
          {glmParameters?.family === 'gaussian' ? (
            <ReportActions
              availableReports={availableReports}
              onAddToReport={onAddToReport}
              onCreateNewReport={onCreateNewReport}
              actionData={{
                type: 'qq_plot',
                title: 'Normal Q-Q Plot',
                qqPlotData: {
                  columnName: createColumnId('standardized_residuals'),
                  dataset: plotDataset
                }
              }}
            >
              <div className={styles.plotWrapper}>
                <QQPlot
                  data={(plotDataset.data[createColumnId('standardized_residuals')] as number[])}
                  width={400}
                  height={300}
                  selectedRows={selectedRows}
                  onPointClick={selectRows}
                />
              </div>
            </ReportActions>
          ) : (
            <div className={styles.plotPlaceholder}>
              <div className={styles.placeholderContent}>
                <h4>Normal Q-Q Plot</h4>
                <p>
                  This plot is only shown for <strong>Gaussian family</strong> GLMs.
                </p>
                <p>
                  For <strong>{glmParameters?.family}</strong> family, residuals are not expected to follow
                  a normal distribution, making this plot not applicable.
                </p>
                <p className={styles.placeholderHint}>
                  Use <strong>Residuals vs Fitted</strong> and <strong>Residuals vs Leverage</strong> instead.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Scale-Location Plot */}
        <div className={styles.plotContainer}>
          <div className={styles.plotTitleRow}>
            <h3 className={styles.plotTitle}>Scale-Location</h3>
            <ContextHelpButtonSimple
              ariaLabel="Scale-Location help - checking constant variance assumption"
              helpContent={
                <div>
                  <h3>Scale-Location Plot</h3>
                  <p>
                    This plot checks <strong>homoscedasticity</strong> (constant variance) by plotting the square root of
                    absolute standardized residuals against fitted values.
                  </p>
                  <h4>What to look for:</h4>
                  <ul>
                    <li><strong>Constant variance</strong>: Points spread evenly around a horizontal line with no clear pattern</li>
                    <li><strong>Increasing variance</strong>: Upward trend (variance increases with fitted values)</li>
                    <li><strong>Decreasing variance</strong>: Downward trend (variance decreases with fitted values)</li>
                    <li><strong>Funnel shape</strong>: Spreading pattern indicates heteroscedasticity</li>
                  </ul>
                  <h4>Why square root?</h4>
                  <p>
                    Taking √|residual| makes patterns easier to see compared to raw residuals, as it reduces the impact
                    of extreme values while preserving the relationship.
                  </p>
                  <h4>Why it matters:</h4>
                  <p>
                    Non-constant variance violates GLM assumptions and can lead to inefficient estimates and incorrect
                    standard errors. You may need to transform the response variable or use a different link function.
                  </p>
                </div>
              }
            />
          </div>
          <ReportActions
            availableReports={availableReports}
            onAddToReport={onAddToReport}
            onCreateNewReport={onCreateNewReport}
            actionData={{
              type: 'scatter_plot',
              title: 'Scale-Location Plot',
              scatterPlotData: {
                xColumn: createColumnId('fitted_values'),
                yColumn: createColumnId('sqrt_abs_std_residuals'),
                dataset: plotDataset
              }
            }}
          >
            <div className={styles.plotWrapper}>
              <ScatterPlot
                dataset={plotDataset}
                xColumn={createColumnId('fitted_values')}
                yColumn={createColumnId('sqrt_abs_std_residuals')}
                width={400}
                height={300}
                selectedRows={selectedRows}
                onPointClick={selectRows}
              />
            </div>
          </ReportActions>
        </div>

        {/* Residuals vs Leverage */}
        <div className={styles.plotContainer}>
          <div className={styles.plotTitleRow}>
            <h3 className={styles.plotTitle}>Residuals vs Leverage</h3>
            <ContextHelpButtonSimple
              ariaLabel="Residuals vs Leverage help - identifying influential observations"
              helpContent={
                <div>
                  <h3>Residuals vs Leverage</h3>
                  <p>
                    This plot identifies <strong>influential observations</strong> that have a large impact on model estimates
                    by combining residuals (fit quality) with leverage (position in predictor space).
                  </p>
                  <h4>Key concepts:</h4>
                  <ul>
                    <li>
                      <strong>Leverage (h)</strong>: How far an observation's X values are from others
                      <ul>
                        <li>Range: 0 to 1</li>
                        <li>High leverage threshold: h {`>`} 2p/n (where p = number of predictors, n = sample size)</li>
                        <li>Interpretation: h = 0.5 means the observation determines 50% of its own predicted value</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Cook's Distance (D)</strong>: Overall measure of influence
                      <ul>
                        <li>Combines residual size and leverage: D = (r²/p) × (h/(1-h)²)</li>
                        <li>D {`>`} 0.5: Moderately influential (investigate)</li>
                        <li>D {`>`} 1.0: Highly influential (serious concern)</li>
                      </ul>
                    </li>
                  </ul>
                  <h4>What to look for:</h4>
                  <ul>
                    <li><strong>Top-right corner</strong>: High leverage + large positive residuals (most influential)</li>
                    <li><strong>Bottom-right corner</strong>: High leverage + large negative residuals (most influential)</li>
                    <li><strong>Outside contours</strong>: Points beyond D = 0.5 or 1.0 lines warrant investigation</li>
                    <li><strong>Left side clustering</strong>: Most points have low leverage (good - uniform influence)</li>
                  </ul>
                  <h4>Why it matters:</h4>
                  <p>
                    Removing a highly influential point can dramatically change model coefficients. These points deserve
                    closer examination - they may be data errors, or they may represent important edge cases.
                  </p>
                </div>
              }
            />
          </div>
          <ReportActions
            availableReports={availableReports}
            onAddToReport={onAddToReport}
            onCreateNewReport={onCreateNewReport}
            actionData={{
              type: 'scatter_plot',
              title: 'Residuals vs Leverage',
              scatterPlotData: {
                xColumn: createColumnId('leverage'),
                yColumn: createColumnId('standardized_residuals'),
                dataset: plotDataset
              }
            }}
          >
            <div className={styles.plotWrapper}>
              <ScatterPlot
                dataset={plotDataset}
                xColumn={createColumnId('leverage')}
                yColumn={createColumnId('standardized_residuals')}
                width={400}
                height={300}
                selectedRows={selectedRows}
                onPointClick={selectRows}
                contourLines={cookContourLines}
                xDomain={residualsVsLeverageDomain?.xDomain}
                yDomain={residualsVsLeverageDomain?.yDomain}
              />
            </div>
          </ReportActions>
        </div>
      </div>

      {/* 選択した点の詳細テーブル */}
      {selectedRows.length > 0 && diagnosticDatasetWithData && (
        <div className={styles.selectedPointsSection}>
          <div className={styles.selectedPointsHeader}>
            <h3>Selected Points: {selectedRows.length}</h3>
            <button
              className={styles.clearButton}
              onClick={() => selectRows([])}
              aria-label="Clear all selected points"
            >
              Clear All
            </button>
          </div>
          <div className={styles.tableWrapper}>
            <table className={styles.selectedPointsTable}>
              <thead>
                <tr>
                  <th>Row</th>
                  {/* 元データの列ヘッダー（response + features） */}
                  {model?.metadata && (() => {
                    const metadata = model.metadata as { target?: string; features?: string[] };
                    const targetName = metadata.target || '';
                    const featureNames = metadata.features || [];

                    return (
                      <>
                        <th>{targetName}</th>
                        {featureNames.map(name => (
                          <th key={name}>{name}</th>
                        ))}
                      </>
                    );
                  })()}
                  {/* 診断統計量の列ヘッダー */}
                  <th>Fitted</th>
                  <th>Residual</th>
                  <th>Leverage</th>
                  <th>Cook's D</th>
                </tr>
              </thead>
              <tbody>
                {selectedRows.map(plotRowIndex => {
                  // プロット行インデックスを元データセット行インデックスにマッピング
                  const originalRowIndex = plotIndexToOriginalIndex[plotRowIndex];

                  // RowNumber列から元の行番号を取得
                  const rowNumberColId = createColumnId('__system_rowid__');
                  const rowNumberData = (diagnosticDatasetWithData.type === 'primary' || diagnosticDatasetWithData.type === 'derived')
                    ? (diagnosticDatasetWithData.data?.[rowNumberColId] as number[] || [])
                    : [];
                  const rowNumber = rowNumberData[originalRowIndex] ?? (originalRowIndex + 1);

                  // 元データの値を取得
                  const metadata = model?.metadata as { target?: string; features?: string[] };

                  // 列名から列IDを取得するヘルパー
                  const getColumnIdByName = (columnName: string): string | null => {
                    const column = diagnosticDatasetWithData.schema.columns.find(col => col.name === columnName);
                    return column?.id || null;
                  };

                  const targetColId = metadata.target ? getColumnIdByName(metadata.target) : null;
                  const featureColIds = (metadata.features || []).map(getColumnIdByName).filter((id): id is string => id !== null);

                  const getOriginalValue = (colId: string | null): string => {
                    if (!colId) return 'N/A';

                    const data = (diagnosticDatasetWithData.type === 'primary' || diagnosticDatasetWithData.type === 'derived')
                      ? diagnosticDatasetWithData.data
                      : undefined;

                    if (!data) return 'N/A';

                    const colData = data[colId as keyof typeof data];
                    if (!colData) return 'N/A';

                    const val = colData[originalRowIndex];
                    if (val === null || val === undefined) return 'null';
                    if (typeof val === 'number') return val.toFixed(4);
                    return String(val);
                  };

                  // 診断統計量を取得
                  const getDiagnosticValue = (colName: string): string => {
                    const colId = createColumnId(colName);
                    const colData = (diagnosticDatasetWithData.type === 'primary' || diagnosticDatasetWithData.type === 'derived')
                      ? (diagnosticDatasetWithData.data?.[colId] as (number | null)[] || [])
                      : [];
                    const val = colData[originalRowIndex];
                    return val !== null && val !== undefined ? val.toFixed(4) : 'N/A';
                  };

                  const residualColName = residualType === 'deviance' ? 'deviance_residuals' : 'pearson_residuals';

                  return (
                    <tr key={plotRowIndex}>
                      <td>{rowNumber}</td>
                      {/* 元データの値 */}
                      <td>{getOriginalValue(targetColId)}</td>
                      {featureColIds.map(colId => (
                        <td key={colId}>{getOriginalValue(colId)}</td>
                      ))}
                      {/* 診断統計量 */}
                      <td>{getDiagnosticValue('fitted_values')}</td>
                      <td>{getDiagnosticValue(residualColName)}</td>
                      <td>{getDiagnosticValue('leverage')}</td>
                      <td>{getDiagnosticValue('cooks_distance')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* アクションボタン */}
      <div className={styles.actionButtons}>
        {/* Add All Plots to Report */}
        {availableReports.length > 0 && (
          <div className={styles.addAllPlotsSection}>
            <select
              className={styles.reportSelect}
              value={selectedReportId}
              onChange={(e) => setSelectedReportId(e.target.value)}
              aria-label="Select report for adding all plots"
            >
              <option value="">Select Report...</option>
              {availableReports.map(report => (
                <option key={report.id} value={report.id}>
                  {report.name}
                </option>
              ))}
            </select>
            <button
              className={styles.addAllPlotsButton}
              onClick={handleAddAllPlotsToReport}
              disabled={!selectedReportId}
              aria-label="Add all diagnostic plots to selected report"
            >
              📊 Add All Plots to Report
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
